import { useState, useMemo, useEffect, useRef } from 'react';
import { PlantPicker } from '../components/PlantPicker.jsx';
import { BuildGridPanel } from '../components/BuildGridPanel.jsx';
import { GlobalNewsPanel } from '../components/GlobalNewsPanel.jsx';
import { FirmCapacityExplainer } from '../components/FirmCapacityExplainer.jsx';
import { useFocusOnMount } from '../components/useFocusOnMount.js';
import {
  selectPlant,
  unselectPlant,
  selectRetirement,
  unselectRetirement,
  commitBuild
} from '../state/actions.js';
import {
  slotsNewThisYear,
  slotsRemainingThisYear,
  currentDemand,
  currentRequiredFirmCapacity,
  projectedOutputRange,
  projectedFirmCapacity,
  prospectiveFirmCapacity,
  prospectiveBuildCapital,
  liveBuildSummary,
  persistentOutputMultiplierForPlant,
  isAdvancedMode
} from '../state/selectors.js';
import { configForState } from '../state/modeConfig.js';
import {
  GAME_YEARS,
  DISASTER_DRAWS_PER_YEAR
} from '../data/constants.js';
import { PLANT_TYPE } from '../data/plants.js';
import { FAMILY } from '../data/families.js';
import { computeProspectivePlantOpex } from '../engine/opex.js';
import { formatTwh, formatGw } from '../components/format.js';
import { useAudio } from '../audio/AudioProvider.jsx';

function CoverageBar({ expected, demand }) {
  const pct = demand > 0 ? Math.min(100, (expected / demand) * 100) : 0;
  const atRisk = expected < demand;
  return (
    <div className="build-coverage-track" aria-hidden="true">
      <div
        className={'build-coverage-fill' + (atRisk ? ' build-coverage-fill-risk' : '')}
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}

const SORT_MODES = [
  { id: 'default', label: 'Capital Cost' },
  { id: 'output', label: 'Output' },
  {
    id: 'cost',
    label: 'Total Cost',
    tooltip: 'Capital cost + effective opex multiplied by the remaining years in the game.'
  },
  { id: 'emissions', label: 'Emissions' },
  {
    id: 'firm',
    label: 'Firm Capacity',
    advancedOnly: true,
    tooltip: 'Effective firm capacity added by the plant at peak demand. Storage is ranked by its rated firm capacity when fully charged.'
  },
  {
    id: 'efficiency',
    label: 'Efficiency',
    tooltip: 'Expected total TWh over the remaining years divided by total cost. Variable plants use average output, adjusted for persistent constraints.'
  },
  { id: 'type', label: 'Type' }
];

const BUILD_SORT_STORAGE_KEY = 'power-grid-build-sort';

function storedBuildSortMode() {
  try {
    return sessionStorage.getItem(BUILD_SORT_STORAGE_KEY) || 'default';
  } catch {
    return 'default';
  }
}

function rememberBuildSortMode(mode) {
  try {
    sessionStorage.setItem(BUILD_SORT_STORAGE_KEY, mode);
  } catch {
    // The current screen still retains the choice when storage is unavailable.
  }
}

const BUILD_ANIMATION_MIN_CAPITAL = 1;
const BUILD_ANIMATION_MAX_CAPITAL = 12;
const BUILD_ANIMATION_MIN_MS = 300;
const BUILD_ANIMATION_MAX_MS = 1500;
const RARE_BUILD_CUE_CHANCE = 1 / 20;

export function buildAnimationDurationMs(capital) {
  const clampedCapital = Math.min(
    BUILD_ANIMATION_MAX_CAPITAL,
    Math.max(BUILD_ANIMATION_MIN_CAPITAL, capital)
  );
  const capitalProgress =
    (clampedCapital - BUILD_ANIMATION_MIN_CAPITAL) /
    (BUILD_ANIMATION_MAX_CAPITAL - BUILD_ANIMATION_MIN_CAPITAL);
  return Math.round(
    BUILD_ANIMATION_MIN_MS +
    capitalProgress * (BUILD_ANIMATION_MAX_MS - BUILD_ANIMATION_MIN_MS)
  );
}

export function buildSelectionCue(plant, random = Math.random) {
  if (!plant || random() >= RARE_BUILD_CUE_CHANCE) return 'financing';
  if (plant.family === FAMILY.FOSSIL_THERMAL) return 'drillBabyDrill';
  if (plant.type === PLANT_TYPE.VARIABLE) return 'electrify';
  return 'financing';
}

export function BuildScreen({ state, dispatch }) {
  const mainRef = useFocusOnMount();
  const [sortMode, setSortMode] = useState(storedBuildSortMode);
  const [newsOpen, setNewsOpen] = useState(false);
  const [constructingPlant, setConstructingPlant] = useState(null);
  const buildTimerRef = useRef(null);
  const { playCue } = useAudio();

  const config = configForState(state);
  const drawCount = DISASTER_DRAWS_PER_YEAR[state.year] ?? 0;
  const remainingDisasters = useMemo(
    () => state.disasterDeck.map((id) => config.disasterById[id]),
    [state.disasterDeck, config]
  );

  const isAdvanced = isAdvancedMode(state);
  const effectiveSortMode = !isAdvanced && sortMode === 'firm'
    ? 'default'
    : sortMode;
  const slotsTotal = slotsNewThisYear(state);
  const slotsLeft = slotsRemainingThisYear(state);
  const slotsUsed = slotsTotal - slotsLeft;
  const demand = currentDemand(state);
  const { mean: expectedOutput } = projectedOutputRange(state);
  const {
    capitalThisYear,
    listedCapitalThisYear,
    resourceDevelopmentPremiumThisYear,
    annualOpex,
    decommissionThisYear
  } =
    liveBuildSummary(state);
  const requiredFirm = isAdvanced ? currentRequiredFirmCapacity(state) : null;
  const firmProjection = isAdvanced ? projectedFirmCapacity(state) : null;
  const remainingYears = GAME_YEARS - state.year + 1;
  const buildLocked = constructingPlant !== null;

  useEffect(() => () => {
    if (buildTimerRef.current) {
      window.clearTimeout(buildTimerRef.current);
    }
  }, []);

  useEffect(() => {
    if (!isAdvanced && sortMode === 'firm') {
      setSortMode('default');
      rememberBuildSortMode('default');
    }
  }, [isAdvanced, sortMode]);

  // Compute dynamic per-plant opex given the current portfolio + pending builds
  const dynamicOpexMap = useMemo(() => {
    const currentPortfolio = [...state.portfolio, ...state.pendingBuilds];
    const map = {};
    for (const plant of config.plants) {
      map[plant.id] = computeProspectivePlantOpex({
        plant,
        currentPortfolio,
        plantsById: config.plantsById,
        subsidyActive: state.subsidyActive,
        eosDuplicateMultiplier: config.eosDuplicateMultiplier,
        eosFirstCopyPaysFull: config.eosFirstCopyPaysFull
      });
    }
    return map;
  }, [state.portfolio, state.pendingBuilds, state.subsidyActive, config]);

  // Same idea for firm capacity: what each plant would actually be
  // worth at the peak if it were built into the current plan.
  const dynamicFirmMap = useMemo(() => {
    if (!isAdvanced) return null;
    const map = {};
    for (const plant of config.plants) {
      map[plant.id] = prospectiveFirmCapacity(state, plant);
    }
    return map;
  }, [isAdvanced, state, config]);

  const dynamicCapitalMap = useMemo(() => {
    if (!isAdvanced) return null;
    const map = {};
    for (const plant of config.plants) {
      map[plant.id] = prospectiveBuildCapital(state, plant.id);
    }
    return map;
  }, [isAdvanced, state, config]);

  const outputMultiplierMap = useMemo(() => {
    const map = {};
    for (const plant of config.plants) {
      map[plant.id] = persistentOutputMultiplierForPlant(state, plant.id);
    }
    return map;
  }, [state.persistentDisasterTargets, config]);

  function handleSelectPlant(id) {
    if (buildLocked || slotsLeft <= 0) return;
    const plant = config.plantsById[id];
    const durationMs = buildAnimationDurationMs(plant.capital);
    playCue(buildSelectionCue(plant));
    setNewsOpen(false);
    setConstructingPlant({ id, durationMs });
    buildTimerRef.current = window.setTimeout(() => {
      dispatch(selectPlant(id));
      setConstructingPlant(null);
      buildTimerRef.current = null;
    }, durationMs);
  }

  function handleSortMode(mode) {
    setSortMode(mode);
    rememberBuildSortMode(mode);
  }

  function handleCommitBuild() {
    if (buildLocked) return;
    playCue('buildConfirmed');
    dispatch(commitBuild());
  }

  function handleRemovePending(idx) {
    if (buildLocked) return;
    playCue('projectCancelled');
    dispatch(unselectPlant(idx));
  }

  function handleRetire(plantId) {
    if (buildLocked) return;
    playCue('projectCancelled');
    dispatch(selectRetirement(plantId));
  }

  function handleUnretire(idx) {
    if (buildLocked) return;
    dispatch(unselectRetirement(idx));
  }

  return (
    <main
      ref={mainRef}
      id="main"
      tabIndex={-1}
      className="screen screen-build"
      aria-busy={buildLocked ? 'true' : undefined}
    >

      {/* Year tabs + phase + demand */}
      <div className="build-top-bar">
        <nav className="build-year-tabs" aria-label="Game years">
          <span className="build-phase-badge">Build Phase</span>
          {Array.from({ length: GAME_YEARS }, (_, i) => i + 1).map((y) => (
            <span
              key={y}
              className={'build-year-tab' + (y === state.year ? ' build-year-tab-active' : '')}
              aria-current={y === state.year ? 'step' : undefined}
            >
              Year {y}
            </span>
          ))}
        </nav>
        <div className="build-top-centre">
          <div className="build-news-wrap">
            <button
              type="button"
              className={
                'build-news-btn' + (newsOpen ? ' build-news-btn-open' : '')
              }
              onClick={() => setNewsOpen((v) => !v)}
              disabled={buildLocked}
              aria-expanded={newsOpen}
              aria-haspopup="dialog"
              aria-controls="global-news-panel"
              data-news-toggle="true"
              data-testid="global-news-button"
            >
              Risk Outlook
            </button>
            {newsOpen ? (
              <GlobalNewsPanel
                id="global-news-panel"
                year={state.year}
                drawCount={drawCount}
                disasters={remainingDisasters}
                riskTierByDisasterId={state.riskTierByDisasterId}
                advanced={isAdvanced}
                onClose={() => setNewsOpen(false)}
              />
            ) : null}
          </div>
          <span
            className={
              'build-subsidy-chip' +
              (state.subsidyActive ? ' build-subsidy-chip-on' : ' build-subsidy-chip-off')
            }
            title={
              state.subsidyActive
                ? 'Fossil fuel subsidy active: fossil opex halved this year'
                : 'Fossil fuel subsidy reformed: no fossil discount'
            }
          >
            {state.subsidyActive
              ? 'Fossil fuel subsidy ON'
              : 'Fossil fuel subsidy OFF'}
          </span>
        </div>
        <div className="build-top-demand">
          <span className="build-top-demand-label">Demand</span>
          <span className="build-top-demand-value">{formatTwh(demand)}</span>
        </div>
      </div>

      {/* Slot indicators + coverage */}
      <div className="build-slots-bar">
        <span className="build-slots-label">Grid Slots</span>
        <span className="build-slot-pips" aria-hidden="true">
          {Array.from({ length: slotsTotal }, (_, i) => (
            <span
              key={i}
              className={'build-slot-pip' + (i < slotsUsed ? ' build-slot-pip-used' : '')}
            />
          ))}
        </span>
        <span className="build-slots-free">
          {slotsLeft} of {slotsTotal} slot{slotsTotal === 1 ? '' : 's'} free
        </span>
        <div className="build-coverage-wrap">
          <span className="build-coverage-label">
            {isAdvanced ? 'Energy' : 'Coverage'}
          </span>
          <CoverageBar expected={expectedOutput} demand={demand} />
          <span className="build-coverage-value">
            {formatTwh(expectedOutput)} / {formatTwh(demand)}
          </span>
        </div>
        {isAdvanced ? (
          <div
            className="build-coverage-wrap build-firm-wrap"
            data-testid="build-firm-capacity"
            tabIndex={0}
          >
            <span className="build-coverage-label">Firm capacity</span>
            <CoverageBar
              expected={firmProjection?.total ?? 0}
              demand={requiredFirm}
            />
            <span
              className={
                'build-coverage-value' +
                ((firmProjection?.total ?? 0) < requiredFirm
                  ? ' build-firm-value-risk'
                  : '')
              }
            >
              {formatGw(firmProjection?.total ?? 0)} /{' '}
              {formatGw(requiredFirm)}
            </span>
            <FirmCapacityExplainer
              projection={firmProjection}
              requiredFirm={requiredFirm}
            />
          </div>
        ) : null}
      </div>

      {/* Sort pills */}
      <div className="build-filter-bar" role="group" aria-label="Sort plants by">
        <span className="build-sort-label">Sort by:</span>
        {SORT_MODES.filter((mode) => isAdvanced || !mode.advancedOnly).map((mode) => {
          const tooltipId = mode.tooltip ? `sort-${mode.id}-tooltip` : undefined;
          return (
            <span key={mode.id} className="build-sort-item">
              <button
                type="button"
                className={
                  'build-filter-pill' +
                  (effectiveSortMode === mode.id ? ' build-filter-pill-active' : '')
                }
                onClick={() => handleSortMode(mode.id)}
                disabled={buildLocked}
                data-testid={`sort-${mode.id}`}
                aria-describedby={tooltipId}
                title={mode.tooltip}
              >
                {mode.label}
              </button>
              {mode.tooltip ? (
                <span
                  id={tooltipId}
                  className="build-sort-tooltip"
                  role="tooltip"
                >
                  {mode.tooltip}
                </span>
              ) : null}
            </span>
          );
        })}
      </div>

      {/* Two-column body */}
      <div className="build-body">
        <div className="build-catalog">
          <h2 className="build-catalog-heading">Plant Investment Options</h2>
          <PlantPicker
            plants={config.plants}
            slotsRemaining={slotsLeft}
            sortMode={effectiveSortMode}
            remainingYears={remainingYears}
            buildLocked={buildLocked}
            constructingPlantId={constructingPlant?.id}
            constructingDurationMs={constructingPlant?.durationMs}
            dynamicOpexMap={dynamicOpexMap}
            dynamicCapitalMap={dynamicCapitalMap}
            dynamicFirmMap={dynamicFirmMap}
            outputMultiplierMap={outputMultiplierMap}
            showFirmCapacity={isAdvanced}
            onSelect={handleSelectPlant}
          />
        </div>
        <aside className="build-grid-panel" aria-label="Your grid">
          <BuildGridPanel
            state={state}
            slotsTotal={slotsTotal}
            slotsUsed={slotsUsed}
            demand={demand}
            estimatedOutput={expectedOutput}
            capitalThisYear={capitalThisYear}
            listedCapitalThisYear={listedCapitalThisYear}
            resourceDevelopmentPremiumThisYear={
              resourceDevelopmentPremiumThisYear
            }
            annualOpex={annualOpex}
            buildLocked={buildLocked}
            plantsById={config.plantsById}
            firmCapacity={firmProjection?.total ?? null}
            requiredFirmCapacity={requiredFirm}
            storageCharge={firmProjection}
            decommissionThisYear={decommissionThisYear}
            onRemovePending={handleRemovePending}
            onRetire={
              isAdvanced && state.portfolio.length > 0 ? handleRetire : undefined
            }
            onUnretire={isAdvanced ? handleUnretire : undefined}
            onCommit={handleCommitBuild}
          />
        </aside>
      </div>

      {buildLocked ? (
        <div
          className="build-interaction-lock"
          aria-hidden="true"
          data-testid="build-interaction-lock"
        />
      ) : null}

    </main>
  );
}
